import { Injectable, signal, computed } from '@angular/core';

@Injectable({ providedIn: 'root' })
export class LoadingService {
  // Tune these to match UX
  private readonly thresholdMs = 300;   // only show if request lasts > 300ms
  private readonly minVisibleMs = 300;  // keep visible at least this long

  // Signals
  private readonly _pending = signal(0);      // raw in-flight count
  private readonly _visible = signal(false);  // what the UI uses
  readonly isLoading = computed(() => this._visible());

  // Timers
  private showTimer: ReturnType<typeof setTimeout> | null = null;
  private hideTimer: ReturnType<typeof setTimeout> | null = null;
  private shownAt = 0;

  /** Called when a new request starts */
  inc(): void {
    // cancel any scheduled hide if a new request starts
    if (this.hideTimer) {
      clearTimeout(this.hideTimer);
      this.hideTimer = null;
    }

    const was = this._pending();
    this._pending.set(was + 1);

    // schedule show only when the first request starts
    if (was === 0) {
      this.scheduleShow();
    }
  }

  /** Called when a request completes or errors */
  dec(): void {
    const next = Math.max(0, this._pending() - 1);
    this._pending.set(next);

    if (next === 0) {
      // cancel pending show if all requests completed before threshold
      if (this.showTimer) {
        clearTimeout(this.showTimer);
        this.showTimer = null;
      }
      this.maybeHide();
    }
  }

  /** Delay showing the spinner to avoid flicker for short requests */
  private scheduleShow(): void {
    if (this.showTimer) return;

    this.showTimer = setTimeout(() => {
      this.showTimer = null;

      // show only if still pending and not visible yet
      if (this._pending() > 0 && !this._visible()) {
        this._visible.set(true);
        this.shownAt = Date.now();
      }
    }, this.thresholdMs);
  }

  /** Hide the spinner when no requests are pending */
  private maybeHide(): void {
    // If visible, enforce a minimum visible duration
    if (this._visible()) {
      const elapsed = Date.now() - this.shownAt;
      const remaining = this.minVisibleMs - elapsed;

      if (remaining > 0) {
        if (this.hideTimer) clearTimeout(this.hideTimer);
        this.hideTimer = setTimeout(() => {
          this.hideTimer = null;
          // re-check in case a new request started
          if (this._pending() === 0) {
            this._visible.set(false);
          }
        }, remaining);
      } else {
        if (this._pending() === 0) {
          this._visible.set(false);
        }
      }
    } else {
      // If never shown, ensure timers cleared
      if (this.showTimer) {
        clearTimeout(this.showTimer);
        this.showTimer = null;
      }
    }
  }
}
